<script setup lang="ts">
import { computed, ref } from 'vue';
import { isEmpty } from 'lodash';
import { useI18n } from 'vue-i18n';

import AttachmentDeleteDialog from '@satellite/components/elements/attachments/AttachmentDeleteDialog.vue';
import AttachmentList from '@satellite/components/elements/attachments/AttachmentList.vue';
import AttachmentUploadButton from '@satellite/components/elements/attachments/AttachmentUploadButton.vue';
import { useAttachmentList } from '@satellite/composables/useAttachmentList';
import { useAttachmentsFeature } from '@satellite/composables/useAttachmentsFeature';
import {
  ATTACHMENT_MAX_SIZE_MB,
  AttachmentRejection,
  type AttachmentRejectionReason
} from '@satellite/helpers/attachmentUtils';
import useNotify from '@/composables/useNotify';
import { getApiErrorMessage } from '@/composables/useTaskAttachments';

import { DatabaseEntities, type Maybe, type YardTaskAttachment } from '@nova/core';

const { taskId = null } = defineProps<{
  taskId?: Maybe<string>;
}>();

const { t } = useI18n();
const { notify } = useNotify();

const { areAttachmentsEnabled } = useAttachmentsFeature();

// Owned by the upload button, read here so the tiles settle while one is in flight.
const isUploading = ref(false);

const { attachments, isLoading, isError, refetch, refreshExpiredUrl } = useAttachmentList({
  relatedEntity: DatabaseEntities.YardTask,
  relatedId: computed(() => taskId)
});

const hasAttachments = computed(() => !isEmpty(attachments.value));
const canUpload = computed(() => areAttachmentsEnabled.value && Boolean(taskId));

// The dialog removes it and clears this itself; holding it here is what opens the dialog.
const attachmentToDelete = ref<Maybe<YardTaskAttachment>>(null);

// Owned by the dialog, read here so the tiles lock their remove affordance while it runs.
const isDeleting = ref(false);

function handleDeleted(): void {
  notify(t('checkTaskDetails.attachments.deleteSuccess'), 'success');
}

/** The API's own message when it sent one, so a refusal reads as more than "it failed". */
function handleDeleteError(error: unknown): void {
  notify(getApiErrorMessage(error, t('checkTaskDetails.attachments.errors.deleteFailed')), 'error');
}

/**
 * Neither opened nor saved: this device has no way to hand a file out at all. Nothing was
 * requested, so unlike a failed download the AxiosPlugin interceptor cannot know about it.
 */
function handleSaveUnavailable(): void {
  notify(t('checkTaskDetails.attachments.errors.saveUnavailable'), 'error');
}

function handleUploaded(count: number): void {
  notify(t('checkTaskDetails.attachments.uploadSuccess', count), 'success');
}

function handleRejected(reason: AttachmentRejectionReason, maxSizeMb: number): void {
  const message =
    reason === AttachmentRejection.TooLarge
      ? t('checkTaskDetails.attachments.errors.tooLarge', { size: maxSizeMb })
      : t('checkTaskDetails.attachments.errors.invalidType');

  notify(message, 'error');
}

function handleUploadError(error: unknown): void {
  notify(getApiErrorMessage(error, t('checkTaskDetails.attachments.errors.uploadFailed')), 'error');
}
</script>

<template>
  <v-card
    variant="flat"
    border
    class="flex-fill"
    :loading="isLoading"
    data-testid="task-attachments">
    <template #loader="{ isActive }">
      <v-progress-linear
        :active="isActive"
        color="primary"
        height="4"
        indeterminate
        :aria-label="t('checkTaskDetails.attachments.loading')"
        data-testid="task-attachments-loading" />
    </template>

    <v-card-text class="d-flex flex-column gap-2">
      <h4 class="typography-heading-md-bold">{{ t('checkTaskDetails.attachments.heading') }}</h4>

      <!--
        Under the tiles the upload button sits in, the way the appointment modal's document
        fields state their rules. Bold falls on the values a user is checking their file
        against; `.pdf` is an extension, so it is the one part no locale rewrites.
      -->
      <i18n-t
        v-if="canUpload"
        keypath="checkTaskDetails.attachments.uploadHint"
        tag="span"
        class="typography-body-sm text--color-text-secondary"
        data-testid="task-attachments-hint">
        <template #size>
          <strong>
            {{ t('checkTaskDetails.attachments.uploadHintSize', { size: ATTACHMENT_MAX_SIZE_MB }) }}
          </strong>
        </template>
        <template #image>
          <strong>{{ t('checkTaskDetails.attachments.uploadHintImages') }}</strong>
        </template>
        <template #pdf>
          <strong>.pdf</strong>
        </template>
      </i18n-t>

      <div
        v-if="isError"
        class="d-flex flex-column align-start gap-1"
        data-testid="task-attachments-error">
        <span class="typography-body-md text--color-text-secondary">
          {{ t('checkTaskDetails.attachments.errors.loadFailed') }}
        </span>
        <v-btn
          variant="text"
          size="small"
          density="compact"
          data-testid="task-attachments-retry"
          @click="refetch()">
          {{ t('checkTaskDetails.attachments.retry') }}
        </v-btn>
      </div>

      <template v-else>
        <attachment-list
          :attachments="attachments"
          :selectable="areAttachmentsEnabled"
          :removable="canUpload"
          :is-removing="isDeleting"
          :remove-label="t('checkTaskDetails.attachments.remove')"
          test-id="task-attachments"
          @remove="attachmentToDelete = $event"
          @error:thumbnail="refreshExpiredUrl($event)"
          @unsupported="handleSaveUnavailable">
          <template #append>
            <attachment-upload-button
              v-if="canUpload"
              v-model:is-uploading="isUploading"
              :related-entity="DatabaseEntities.YardTask"
              :related-id="taskId"
              :label="t('checkTaskDetails.attachments.upload')"
              test-id="task-attachments"
              @uploaded="handleUploaded"
              @rejected="handleRejected"
              @error="handleUploadError" />
          </template>

          <template #preview-error>
            <span class="typography-body-md text--color-text-secondary">
              {{ t('checkTaskDetails.attachments.errors.previewFailed') }}
            </span>
          </template>
        </attachment-list>

        <attachment-delete-dialog
          v-if="canUpload"
          v-model="attachmentToDelete"
          v-model:is-deleting="isDeleting"
          :related-entity="DatabaseEntities.YardTask"
          :related-id="taskId"
          :title="t('checkTaskDetails.attachments.deleteTitle')"
          :message="t('checkTaskDetails.attachments.deleteWarning')"
          :confirm-label="t('checkTaskDetails.attachments.deleteConfirm')"
          :cancel-label="t('checkTaskDetails.attachments.deleteCancel')"
          test-id="task-attachments"
          @deleted="handleDeleted"
          @error="handleDeleteError" />

        <!--
          Stated on its own terms rather than as the other branch of anything above: only when
          no tile stands in for it, and never while a first load could still bring some in.
        -->
        <span
          v-if="!canUpload && !hasAttachments && !isLoading"
          class="typography-body-md text--color-text-secondary"
          data-testid="task-attachments-empty">
          {{ t('checkTaskDetails.attachments.empty') }}
        </span>
      </template>
    </v-card-text>
  </v-card>
</template>
